/**
 * THE RECALCULATION LINE'S DATA (A2-1b-2) — `GET /{id}/recompute-status` and its
 * "Try again".
 *
 * The server reads the mc-worker's job rows, so this only ever says something for
 * orgs whose recalculations run on the worker (`MC_RECOMPUTE_ON_WORKER`); everyone
 * else reads `idle`, which hides the line, as today.
 *
 * WHEN IT ASKS. Once when the page opens, at once whenever a gesture announces a
 * recompute (`useOnRecompute`), every 5 s while a job runs and every 15 s while
 * one waits for a worker — then it stops. The endpoint is a few rows, never the node matrix `/run` ships,
 * which is why polling it is affordable where polling `/run` is not.
 *
 * WHEN A JOB ENDS, the declaration's reads are invalidated once: a page opened while
 * a job was already running has no armed `/run` poll (N11 arms it on a gesture), so
 * without this it would never learn the recalculation finished.
 */
import { PERMISSIONS } from "@/constants/permissions"
import { useGuardedMutation } from "@/hooks/useGuardedMutation"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useRef } from "react"
import { apiClientV2 } from "../../../services/api/apiClient"
import { invalidateDeclaration } from "./declarationsApi"
import { useOnRecompute } from "./recomputeSignal"

export type RecomputeStatusKind = "idle" | "queued" | "running" | "failed"

export type RecomputeStatus = {
  status: RecomputeStatusKind
  /** ISO time the shown state began (running: started, queued: first requested,
   *  failed: ended). */
  since: string | null
  /** The cone kind being recalculated (`resolve_scope`, ...). */
  kind: string | null
  attempts: number
  /** Failed only: "Try again" is offered (a retry that failed again cools down). */
  retryable: boolean
}

/** How often to ask again while a job runs, and while it waits for a worker
 *  (nothing changes until one picks it up, so that wait is polled more slowly). */
export const RECOMPUTE_STATUS_POLL_RUNNING_MS = 5_000
export const RECOMPUTE_STATUS_POLL_QUEUED_MS = 15_000

/** OUTSIDE the declaration's `["mc", "declarations", declId]` prefix, on purpose:
 *  the footer reads any fetch under that prefix as "Saving …"
 *  (`StudioV2FooterBar`'s `useIsFetching`), and this query polls every few
 *  seconds while a job is open. A gesture asks for it directly instead
 *  (`useOnRecompute`), so it does not need `invalidateDeclaration` to reach it. */
export function recomputeStatusKey(declId: string) {
  return ["mc", "recompute-status", declId] as const
}

const isOpen = (status: RecomputeStatusKind | undefined) =>
  status === "queued" || status === "running"

export function useRecomputeStatus(declId: string) {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: recomputeStatusKey(declId),
    enabled: !!declId,
    queryFn: async (): Promise<RecomputeStatus> =>
      apiClientV2
        .get(`/material-compliance/declarations/${declId}/recompute-status`)
        .then((r) => r.data as RecomputeStatus),
    refetchInterval: (q) =>
      q.state.data?.status === "running"
        ? RECOMPUTE_STATUS_POLL_RUNNING_MS
        : q.state.data?.status === "queued"
          ? RECOMPUTE_STATUS_POLL_QUEUED_MS
          : false,
  })

  const refetch = query.refetch
  const askNow = useCallback(() => {
    void refetch()
  }, [refetch])
  useOnRecompute(declId, askNow)

  const current = query.data?.status
  const previous = useRef(current)
  useEffect(() => {
    if (isOpen(previous.current) && !isOpen(current) && current) {
      invalidateDeclaration(queryClient, declId, { cause: "recompute" })
    }
    previous.current = current
  }, [current, declId, queryClient])

  return query
}

/** "Try again" on a failed recalculation. A 409 (nothing left to retry, e.g. a
 *  second press) is answered by asking for the status again, not by an error. */
export function useRetryRecompute() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: async (declId: string): Promise<void> => {
      await apiClientV2.post(
        `/material-compliance/declarations/${declId}/recompute/retry`,
      )
    },
    onSettled: (_data, _error, declId) => {
      void queryClient.invalidateQueries({
        queryKey: recomputeStatusKey(declId),
      })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_DECLARATIONS_REVIEW)
}
